<!-- Ported from the Polaris kit: src/product/components/ResponseCard.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first. -->
<script setup>
// One answer the agent may give: the question, the answer, where it came from and whether a person has approved it. A pending one
// waits for approval and is not used until then.
import { useI18n } from 'vue-i18n';
import { Checkmark16, Edit16, TrashCan16 } from '@carbon/icons-vue';
import { Badge } from '../ui/badge';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import RowMenu from './RowMenu.vue';

const props = defineProps({
  question: { type: String, required: true },
  answer: { type: String, required: true },
  status: { type: String, default: 'approved' }, // approved | pending
  source: { type: String, default: '' }, // the document it came from
  updated: { type: String, default: '' },
  selectable: Boolean,
  selected: Boolean,
});

const emit = defineEmits(['select', 'action']);

const { t } = useI18n();

const items = [
  ...(props.status === 'pending'
    ? [
        {
          label: t('POLARIS.CARDS.APPROVE'),
          value: 'approve',
          icon: Checkmark16,
        },
      ]
    : []),
  { label: t('POLARIS.CARDS.EDIT'), value: 'edit', icon: Edit16 },
  {
    label: t('POLARIS.CARDS.DELETE'),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
];
</script>

<template>
  <Card class="gap-2 py-4" data-response-card>
    <CardHeader class="grid-cols-[auto_1fr_auto] items-start gap-2 px-4">
      <label v-if="selectable" class="flex size-6 items-center justify-center">
        <Checkbox
          :model-value="selected"
          :aria-label="t('POLARIS.CARDS.SELECT', { item: question })"
          @update:model-value="emit('select')"
        />
      </label>
      <CardTitle class="col-start-2 line-clamp-2">{{ question }}</CardTitle>
      <RowMenu
        class="col-start-3"
        :label="`Actions for: ${question}`"
        :items="items"
        @action="value => emit('action', { action: value })"
      />
    </CardHeader>
    <CardContent class="px-4">
      <p class="line-clamp-3 text-polaris-muted-foreground">
        {{ answer }}
      </p>
    </CardContent>
    <CardFooter class="flex-wrap gap-2 px-4">
      <Badge :variant="status === 'pending' ? 'warning' : 'success'">
        {{
          status === 'pending'
            ? t('POLARIS.CARDS.WAITING_APPROVAL')
            : t('POLARIS.CARDS.APPROVED')
        }}
      </Badge>
      <p
        v-if="source"
        class="type-polaris-caption min-w-0 truncate text-polaris-muted-foreground"
      >
        {{ t('POLARIS.CARDS.FROM_SOURCE', { source }) }}
      </p>
      <p
        v-if="updated"
        class="type-polaris-caption ml-auto text-polaris-muted-foreground"
      >
        {{ updated }}
      </p>
    </CardFooter>
  </Card>
</template>
